import React from 'react';

// Story data - easily configurable
const storiesData = [
  {
    id: 1,
    name: "Lisa Kudrow",
    startingPoint: "Biology researcher turned reluctant actress",
    story: "Studied biology and worked in medical research before finding comedy through improv, after early acting setbacks. What looked like a detour became the source of Phoebe Buffay's originality.",
    initials: "LK",
    accentColor: "#A78BFA",
  },
  {
    id: 2,
    name: "Steve Jobs",
    startingPoint: "College dropout who wandered into typography",
    story: "Dropped out of college and wandered into a typography class with no career plan. That curiosity later shaped the Macintosh and modern digital text.",
    initials: "SJ",
    accentColor: "#60A5FA",
  },
  {
    id: 3,
    name: "Harrison Ford",
    startingPoint: "Carpenter who built cabinets for filmmakers",
    story: "Worked as a carpenter for years, including building cabinets for filmmakers, before landing acting roles later than most. His 'day job' put him in the right rooms.",
    initials: "HF",
    accentColor: "#F472B6",
  },
  {
    id: 4,
    name: "Vera Wang",
    startingPoint: "Figure skater turned fashion journalist",
    story: "Trained as a competitive figure skater, then worked in fashion journalism for years before designing her first dress at age 40. She entered the industry sideways and reshaped it.",
    initials: "VW",
    accentColor: "#4ECDC4",
  },
  {
    id: 5,
    name: "Julia Child",
    startingPoint: "Intelligence officer who discovered cooking at 37",
    story: "Studied history and worked in intelligence during WWII before discovering French cooking in her mid-30s. She didn't publish her first cookbook until her 40s.",
    initials: "JC",
    accentColor: "#FB923C",
  },
  {
    id: 6,
    name: "Jack Ma",
    startingPoint: "Rejected English teacher with no tech background",
    story: "Rejected from jobs and universities, trained as an English teacher, and failed early internet ventures before founding Alibaba with no technical background.",
    initials: "JM",
    accentColor: "#34D399",
  },
  {
    id: 7,
    name: "Hedy Lamarr",
    startingPoint: "Hollywood actress and secret inventor",
    story: "Known first as a Hollywood actress, she independently co-invented frequency-hopping technology that later became foundational to Wi-Fi and Bluetooth.",
    initials: "HL",
    accentColor: "#E879F9",
  },
  {
    id: 8,
    name: "Andrea Bocelli",
    startingPoint: "Lawyer who sang at night",
    story: "Studied law and performed music at night, briefly practicing as a lawyer before committing fully to singing after years of informal training.",
    initials: "AB",
    accentColor: "#FBBF24",
  },
];

// Individual Story Card Component
const StoryCard = ({ story, index }) => {
  const isEven = index % 2 === 0;

  return (
    <div className="group relative">
      {/* Subtle accent line */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-300"
        style={{ backgroundColor: story.accentColor }}
      />

      <div className="pl-8 md:pl-10 py-6">
        {/* Header with initials and name */}
        <div className="flex items-center gap-4 mb-4">
          {/* Initials Circle */}
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${story.accentColor} 0%, ${story.accentColor}dd 100%)`,
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            {story.initials}
          </div>

          <div>
            {/* Name */}
            <h3
              className="text-xl md:text-2xl font-bold text-[#1a1a1a] leading-tight"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {story.name}
            </h3>

            {/* Starting Point */}
            <p
              className="text-sm font-medium mt-0.5"
              style={{ color: story.accentColor, fontFamily: "'Inter', sans-serif" }}
            >
              {story.startingPoint}
            </p>
          </div>
        </div>

        {/* Story */}
        <p
          className="text-[#1a1a1a]/75 text-base md:text-lg leading-relaxed max-w-2xl"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {story.story}
        </p>
      </div>
    </div>
  );
};

// Main Component
const NonLinearPaths = ({ stories = storiesData }) => {
  return (
    <section className="w-full py-16 md:py-24" style={{ backgroundColor: '#FFFDF8' }}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <p
            className="text-[#FF6B6B] text-sm font-bold uppercase tracking-widest mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Careers Aren't Straight Lines
          </p>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1a1a1a] mb-6 leading-tight"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Non-Linear Paths That<br className="hidden md:block" /> Changed Everything
          </h2>
          <p
            className="text-[#1a1a1a]/60 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            The detours, side quests, and "wasted" years that turned out to be anything but.
          </p>
        </div>

        {/* Stories Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-4">
          {stories.map((story, index) => (
            <div key={story.id}>
              <StoryCard story={story} index={index} />

              {/* Soft separator - only between items in single column, or between row pairs */}
              {index < stories.length - 1 && (
                <div className="lg:hidden">
                  <div className="h-px bg-gradient-to-r from-transparent via-[#1a1a1a]/10 to-transparent my-2" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Closing Reflection */}
        <div className="mt-16 md:mt-24 text-center">
          <div className="inline-block px-8 py-6 rounded-2xl bg-white/60 backdrop-blur-sm shadow-sm border border-[#1a1a1a]/5">
            <p
              className="text-[#1a1a1a]/70 text-base md:text-lg italic max-w-xl leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              "The dots only connect looking backward. Curiosity, failure, and lateral moves often compound into long-term advantage."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NonLinearPaths;
